import React, { useState, useEffect } from 'react';
import invoiceService from '../services/invoiceService';
import facilityService from '../services/facilityService';
import './ReportsPage.css';

const ReportsPage = () => {
  const [facilities, setFacilities] = useState([]);
  const [error, setError] = useState('');
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [facilityId, setFacilityId] = useState('');
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    const loadFacilities = async () => {
      try {
        const data = await facilityService.getAllFacilities();
        setFacilities(data);
      } catch (err) {
        console.error('Error loading facilities:', err);
      }
    };
    loadFacilities();
  }, []);

  const requireFacility = () => {
    if (!facilityId) {
      setError('Bitte wählen Sie eine Anlage');
      return false;
    }
    setError('');
    return true;
  };

  const handleDailyReport = async () => {
    if (!requireFacility()) return;
    try {
      await invoiceService.getDailyReport(reportDate, facilityId);
    } catch (err) {
      setError('Fehler beim Erstellen des Tagesabschlusses: ' + err.message);
    }
  };

  const handleDailyReportCsv = async () => {
    if (!requireFacility()) return;
    try {
      await invoiceService.getDailyReportCsv(reportDate, facilityId);
    } catch (err) {
      setError('Fehler beim Erstellen des Kassenberichts: ' + err.message);
    }
  };

  const handleLoadSummary = async () => {
    if (!requireFacility()) return;
    try {
      setSummaryLoading(true);
      setSummary(null);
      const data = await invoiceService.getCashbookDailySummary(facilityId, reportDate);
      setSummary(data);
    } catch (err) {
      setError('Fehler beim Laden der Kassenbuch-Tagessumme: ' + err.message);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleInvoiceCsv = async () => {
    try {
      const params = {
        date_from: reportDate,
        date_to: reportDate,
        ...(facilityId && { facility_id: facilityId })
      };
      await invoiceService.exportCsv(params);
    } catch (err) {
      setError('Fehler beim CSV-Export: ' + err.message);
    }
  };

  return (
    <div className="reports-page">
      <header className="page-header">
        <div className="header-content">
          <div>
            <h1>Berichte</h1>
            <p>Statistiken und Auswertungen</p>
          </div>
        </div>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="report-controls">
        <div className="filter-group">
          <label htmlFor="report-date">Datum:</label>
          <input
            id="report-date"
            type="date"
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="report-facility">Anlage:</label>
          <select
            id="report-facility"
            value={facilityId}
            onChange={(e) => setFacilityId(e.target.value)}
          >
            <option value="">Anlage wählen</option>
            {facilities.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="report-section">
        <h2>Tagesabschluss</h2>
        <p className="report-hint">Kassenbericht für die gewählte Anlage und das Datum herunterladen.</p>
        <div className="report-actions">
          <button className="report-btn" onClick={handleDailyReport}>
            📊 Tagesabschluss (PDF)
          </button>
          <button className="report-btn" onClick={handleDailyReportCsv}>
            📥 Kassenbericht (CSV)
          </button>
        </div>
      </div>

      <div className="report-section">
        <h2>Kassenbuch-Tagessumme</h2>
        <p className="report-hint">Einnahmen und Ausgaben der gewählten Anlage für das Datum.</p>
        <div className="report-actions">
          <button className="report-btn" onClick={handleLoadSummary}>
            🔄 Tagessumme laden
          </button>
        </div>

        {summaryLoading ? (
          <div className="loading">Laden...</div>
        ) : summary ? (
          <div className="summary-grid">
            <div className="summary-card">
              <h3>Einnahmen</h3>
              <table className="summary-table">
                <tbody>
                  <tr>
                    <td>Bar</td>
                    <td>{invoiceService.formatCurrency(summary.income.cash)}</td>
                  </tr>
                  <tr className="summary-total-row">
                    <td>Gesamt</td>
                    <td>{invoiceService.formatCurrency(summary.income.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="summary-card summary-balance">
              <h3>Kassenendstand (Bar)</h3>
              <div className="summary-balance-value">
                {invoiceService.formatCurrency(summary.income.cash)}
              </div>
            </div>
          </div>
        ) : (
          <div className="no-data">Noch keine Tagessumme geladen</div>
        )}
      </div>

      <div className="report-section">
        <h2>Rechnungs-Export</h2>
        <p className="report-hint">Rechnungen des gewählten Datums als CSV exportieren.</p>
        <div className="report-actions">
          <button className="report-btn" onClick={handleInvoiceCsv}>
            📥 Rechnungen (CSV)
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
